import { useEffect, useState } from "react";
import "./autocomplete.css";
import IAutoCompleteObject from "../models/IAutoCompleteObject";
import api from "../services/api";

interface Params {
  suggestions: IAutoCompleteObject[];
  urlFetch: string;
  defaultValue?: string | IAutoCompleteObject;
  onAfterChange?: any;
}

const AutoComplete = ({
  suggestions,
  urlFetch,
  defaultValue,
  onAfterChange,
}: Params) => {
  const [filteredSuggestions, setFilteredSuggestions] =
    useState(Array<IAutoCompleteObject>());
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [delayedSearchTerm, setDelayedSearchTerm] = useState("");
  const [allowSearch, setAllowSearch] = useState(false);

  useEffect(() => {
    if (typeof defaultValue === "string") {
      setSearchTerm(defaultValue);
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      setActiveSuggestionIndex(0);
      setAllowSearch(false);
      return;
    }

    setSearchTerm(defaultValue?.label ?? "");
    setFilteredSuggestions([]);
    setShowSuggestions(false);
    setActiveSuggestionIndex(0);
    setAllowSearch(false);
  }, [defaultValue]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDelayedSearchTerm(searchTerm);
    }, 500); // You can adjust the delay time according to your preferences

    return () => clearTimeout(delaySearch);
  }, [searchTerm]);

  useEffect(() => {
    if (!allowSearch) {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Avoid side-effects when just hydrating with default value.
    if (!delayedSearchTerm || delayedSearchTerm.trim().length < 3) {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Perform the search operation with delayedSearchTerm.
    onChange(delayedSearchTerm);
  }, [allowSearch, delayedSearchTerm]);

  useEffect(() => {
    if (!showSuggestions) {
      return;
    }

    const hideSuggestionsTimeout = setTimeout(() => {
      setSearchTerm("");
      setDelayedSearchTerm("");
      setAllowSearch(false);
      setFilteredSuggestions([]);
      setActiveSuggestionIndex(0);
      setShowSuggestions(false);
      afterSelect(undefined);
    }, 5000);

    return () => clearTimeout(hideSuggestionsTimeout);
  }, [showSuggestions]);

  useEffect(() => {
    const normalizedTerm = searchTerm.trim();
    const shouldClearByNoResult =
      allowSearch &&
      !searching &&
      normalizedTerm.length >= 3 &&
      filteredSuggestions.length === 0;

    if (!shouldClearByNoResult) {
      return;
    }

    const clearInputTimeout = setTimeout(() => {
      setSearchTerm("");
      setDelayedSearchTerm("");
      setAllowSearch(false);
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      setActiveSuggestionIndex(0);
      afterSelect(undefined);
    }, 5000);

    return () => clearTimeout(clearInputTimeout);
  }, [allowSearch, filteredSuggestions.length, searchTerm, searching]);

  const handleInputChange = (event: any) => {
    setAllowSearch(true);
    setSearchTerm(event.target.value);

    if (!event.target.value || event.target.value.trim().length < 3) {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      setActiveSuggestionIndex(0);
    }
  };

  const onChange = (userInput: string) => {
    if (!searching) setSearchTerm(userInput);

    if (userInput && userInput.length > 2) {
      setSearching(true);

      try {
        api.get(`${urlFetch}${userInput.toUpperCase()}`).then((result) => {
          suggestions = result.data.data.map((d: any) => {
            return { id: d.id, label: d.name };
          });
          setSearching(false);

          filterResults(userInput);
        });
      } catch (error) {
        setSearching(false);
      }
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      setActiveSuggestionIndex(0);
    }
  };

  const filterResults = (userInput: string) => {
    // Filter our suggestions that don't contain the user's input
    const unLinked = suggestions.filter(
      (suggestion) =>
        suggestion?.label.toUpperCase().indexOf(userInput.toUpperCase()) > -1,
    );

    setFilteredSuggestions(unLinked ?? []);
    setActiveSuggestionIndex(0);
    setShowSuggestions(unLinked && unLinked.length > 0);
  };

  const onClick = (suggestion: any) => {
    setFilteredSuggestions([]);
    setSearchTerm(suggestion.label);
    setActiveSuggestionIndex(0);
    setShowSuggestions(false);
    setAllowSearch(false);

    afterSelect(suggestion);
  };

  const SuggestionsListComponent = () => {
    return filteredSuggestions.length ? (
      <ul className="suggestions">
        {filteredSuggestions.map((suggestion: IAutoCompleteObject, index) => {
          let className;
          // Flag the active suggestion with a class
          if (index === activeSuggestionIndex) {
            className = "suggestion-active";
          }
          return (
            <li
              className={className}
              key={suggestion.id}
              onClick={() => onClick(suggestion)}
            >
              {suggestion.label}
            </li>
          );
        })}
      </ul>
    ) : (
      <div className="no-suggestions"></div>
    );
  };

  const onKeyDown = (key: any) => {
    if (key.keyCode === 40) {
      //down arrow
      setActiveSuggestionIndex(activeSuggestionIndex + 1);
    }

    if (key.keyCode === 13 || key.keyCode === 9) {
      const l = filteredSuggestions[activeSuggestionIndex];
      setSearchTerm(l.label);
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      setAllowSearch(false);
      afterSelect(l);
    }
  };

  const onBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 150);
  };

  const afterSelect = (inputValue?: IAutoCompleteObject) => {
    if (onAfterChange) {
      onAfterChange(inputValue);
    }
  };

  return (
    <div className="autocomplete-wrapper">
      <input
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={onKeyDown}
        className="uk-input uk-form-small"
        style={{ textTransform: "uppercase" }}
        onBlur={onBlur}
        disabled={searching}
      />
      {showSuggestions && <SuggestionsListComponent />}
    </div>
  );
};
export default AutoComplete;
